import { z } from 'zod'
import {
  DataUpdatePayload,
  StrictDataUpdateV1Payload,
  TripleUpdatePayload,
  dataUpdatePayloadSchema,
  strictDataUpdateV1PayloadSchema,
  tripleUpdatePayloadSchema,
} from '../../../../shared/data-update/data-update-schema'
import { Logging } from '../../../../shared/data-update/from-n3-quads'

/**
 * bridge-protocol.ts
 *
 * The message contract between the host (CodeWidget) and the embedded page,
 * plus the pure transforms that back it (JSON serialization for script
 * inlining, and the page's triples -> data-update payload conversion).
 *
 * Messages travel over `window.postMessage`:
 *   page  -> host : { __widgetUp: true, type, payload }   (see IncomingMessage)
 *   host  -> page : { __widget:   true, type, ... }        (see OutgoingMessage)
 *
 * Keeping the protocol here (separate from the React/DOM wiring) means it can
 * be reasoned about and unit-tested without a host or an iframe.
 */

/** Any JSON-serializable value (config blob, coerced config-field value, ...). */
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue }

// --- page -> host ----------------------------------------------------------

const setParamPayloadSchema = z.object({
  params: z.record(z.unknown()),
})

const selectPayloadSchema = z.object({
  query: z.string(),
  requestId: z.string().nonempty(),
})

/**
 * One triple, exactly as the data-update endpoint takes it, except that `graph`
 * may be left out: the host fills it from the request's `graph` or the widget's
 * targetGraph before validating against the endpoint's own schema.
 */
const pageTripleSchema = tripleUpdatePayloadSchema.partial({ graph: true })

export type PageTriple = z.infer<typeof pageTripleSchema>

/**
 * `wistor.updateQuads(args)`.
 *
 * `variant` is the discriminator, and it is the same string the backend
 * discriminates the payload on, so page, host and servlet stay in step:
 *
 *   { variant: 'legacy',    updates?, deletes?, graph? }
 *   { variant: 'strict-v1', inserts?, deletes?, graph? }
 *
 * Legacy replaces every existing value of a subject+predicate; strict-v1 only
 * removes the exact triples in `deletes`. Parsed loosely on purpose: a bad or
 * unknown variant has to reach the transform so the page gets a real error
 * back, instead of the message being dropped and its promise never settling.
 *
 * `updateQuads({ inserts, deletes })` with no variant runs as legacy, with
 * `inserts` read as `updates`, so a page need not name a variant to write the
 * way this API always wrote. Pages predating this contract passed Turtle strings
 * rather than triples and are rejected by the schema, which is the intent: the
 * failure is a validation error naming the field, not a silent misread.
 */
const updateQuadsPayloadSchema = z.object({
  variant: z.string().optional(),
  /** Legacy field name — overwrite semantics. */
  updates: z.array(pageTripleSchema).optional(),
  /** strict-v1 field name; also accepted as a legacy alias for `updates`. */
  inserts: z.array(pageTripleSchema).optional(),
  deletes: z.array(pageTripleSchema).optional(),
  /** Graph for triples that name none of their own. Overrides the widget's targetGraph. */
  graph: z.string().optional(),
  requestId: z.string().nonempty(),
})

const readFilePayloadSchema = z.object({
  /** Path under the current repo's download root — never a host or a repo, so the page cannot retarget the read. */
  path: z.string().nonempty(),
  requestId: z.string().nonempty(),
})

/**
 * `wistor.uploadRdf(args)`. Bulk-loads a whole RDF document, where updateQuads
 * sends a diff of individual triples.
 *
 * postMessage clones a Blob or File into the host realm, so a file input's value
 * can be handed over without being read into a string first.
 */
const uploadRdfPayloadSchema = z.object({
  content: z.union([z.string(), z.instanceof(Blob)]),
  /** Omit for a File and it is taken from the filename; otherwise defaults to Turtle. */
  format: z.enum(['turtle', 'trig', 'n3']).optional(),
  /**
   * Target graph, overriding the widget's targetGraph. Turtle and N3 land here
   * entirely; a TriG payload puts only its default-graph triples here, its named
   * graphs going where they name. Write permission is checked per graph by the
   * backend, which reads the graph names out of the payload itself.
   */
  graph: z.string().optional(),
  requestId: z.string().nonempty(),
})

/**
 * `wistor.uploadFile(args)`. Stores an opaque document -- a PDF, an image, a
 * spreadsheet -- and writes `uploadanyfile:` metadata triples describing it,
 * where uploadRdf loads triples into the store and updateQuads sends a diff.
 *
 * postMessage clones a Blob or File into the host realm, so a file input's
 * value can be handed over without being read into a string first.
 */
const uploadFilePayloadSchema = z.object({
  /**
   * The bytes themselves, not the Blob or File they came from.
   *
   * A File picked inside the sandboxed frame carries an OS-level read grant
   * held by that frame. The handle survives the structured clone intact, but
   * the host is a different security context: reading it there is refused with
   * net::ERR_ACCESS_DENIED, which surfaces only as "Network Error" once the
   * upload is already in flight. So the page reads the file itself (see
   * bootstrap.ts) and an ArrayBuffer -- copied by the clone, not referenced --
   * crosses instead.
   */
  bytes: z.instanceof(ArrayBuffer),
  /** Taken from the File's own name when the page did not pass one. */
  filename: z.string().nonempty(),
  /** The Blob's type, so the host can rebuild it without losing the media type. */
  mediaType: z.string().optional(),
  /** Metadata graph for the file's triples, overriding the widget's targetGraph. Write permission is checked by the backend. */
  graph: z.string().optional(),
  /** Subject this file belongs to, recorded as uploadanyfile:belongsTo. */
  belongsTo: z.string().optional(),
  requestId: z.string().nonempty(),
})

const callRulePayloadSchema = z.object({
  /** Identifies the rule to invoke. */
  ruleTag: z.string().nonempty(),
  /** The rule's arguments: a flat string->string map. */
  params: z.record(z.string()),
  requestId: z.string().nonempty(),
})

const toastPayloadSchema = z.object({
  message: z.string(),
  variant: z.enum(['info', 'success', 'warning', 'error']).optional(),
})

/**
 * `wistor.openUrl(url, { inPlace })`. Fire-and-forget, like `toast`: no
 * requestId, because the page gets no answer back. The URL is page-supplied and
 * therefore untrusted — the host, not this schema, decides which schemes and
 * which in-place targets may open (see onOpenUrl in code-widget.tsx).
 */
const openUrlPayloadSchema = z.object({
  url: z.string().nonempty(),
  inPlace: z.boolean().optional(),
})

const getViewerConfigPayloadSchema = z.object({
  requestId: z.string().nonempty(),
})

/**
 * `wistor.exportAsFile(content, filename, opts)`. Hands the person a file the page built: a template, an export.
 *
 * The page turns its content into bytes (see bootstrap.ts) and the host makes the save, in its own document. The page
 * builds no blob: URL and no link of its own, so the save reads as one literal call to the Lock&Publish check instead
 * of a computed navigation. The name and type are page-supplied: the name goes to the <a download> as is, and the
 * browser makes it safe (no path, no characters the file system refuses) and may still change it, e.g. "(1)" for a
 * name already taken. The host only ever saves — nothing is opened or navigated to.
 */
const exportAsFilePayloadSchema = z.object({
  /** The bytes, copied by the structured clone, as for uploadFile. */
  bytes: z.instanceof(ArrayBuffer),
  /** Required: a file saved as "download" tells the person nothing. */
  filename: z.string().nonempty(),
  /** The page's mimeType option, else the Blob's type; empty means: from the filename's extension (exportMediaType). */
  mediaType: z.string().optional(),
  requestId: z.string().nonempty(),
})

export type SetParamPayload = z.infer<typeof setParamPayloadSchema>
export type SelectPayload = z.infer<typeof selectPayloadSchema>
export type UpdateQuadsPayload = z.infer<typeof updateQuadsPayloadSchema>
export type ReadFilePayload = z.infer<typeof readFilePayloadSchema>
export type UploadRdfPayload = z.infer<typeof uploadRdfPayloadSchema>
export type UploadFilePayload = z.infer<typeof uploadFilePayloadSchema>
export type CallRulePayload = z.infer<typeof callRulePayloadSchema>
export type ToastPayload = z.infer<typeof toastPayloadSchema>
export type OpenUrlPayload = z.infer<typeof openUrlPayloadSchema>
export type GetViewerConfigPayload = z.infer<typeof getViewerConfigPayloadSchema>
export type ExportAsFilePayload = z.infer<typeof exportAsFilePayloadSchema>

export type IncomingMessage =
  | { type: 'set-param'; payload: SetParamPayload }
  | { type: 'sparql-select'; payload: SelectPayload }
  | { type: 'update-quads'; payload: UpdateQuadsPayload }
  | { type: 'read-file'; payload: ReadFilePayload }
  | { type: 'upload-rdf'; payload: UploadRdfPayload }
  | { type: 'upload-file'; payload: UploadFilePayload }
  | { type: 'call-rule'; payload: CallRulePayload }
  | { type: 'toast'; payload: ToastPayload }
  | { type: 'open-url'; payload: OpenUrlPayload }
  | { type: 'get-viewer-config'; payload: GetViewerConfigPayload }
  | { type: 'export-as-file'; payload: ExportAsFilePayload }

const incomingEnvelopeSchema = z.object({
  __widgetUp: z.literal(true),
  type: z.string(),
  payload: z.unknown(),
})

/**
 * Parse an untrusted `MessageEvent.data` into a typed IncomingMessage.
 * Returns null for anything that isn't a well-formed widget-up message, so the
 * caller can ignore unrelated postMessages without a try/catch.
 */
export function parseIncoming(raw: unknown): IncomingMessage | null {
  const envelope = incomingEnvelopeSchema.safeParse(raw)
  if (!envelope.success) return null

  const { type, payload } = envelope.data
  switch (type) {
    case 'set-param': {
      const parsed = setParamPayloadSchema.safeParse(payload)
      return parsed.success ? { type, payload: parsed.data } : null
    }
    case 'sparql-select': {
      const parsed = selectPayloadSchema.safeParse(payload)
      return parsed.success ? { type, payload: parsed.data } : null
    }
    case 'update-quads': {
      const parsed = updateQuadsPayloadSchema.safeParse(payload)
      return parsed.success ? { type, payload: parsed.data } : null
    }
    case 'read-file': {
      const parsed = readFilePayloadSchema.safeParse(payload)
      return parsed.success ? { type, payload: parsed.data } : null
    }
    case 'upload-rdf': {
      const parsed = uploadRdfPayloadSchema.safeParse(payload)
      return parsed.success ? { type, payload: parsed.data } : null
    }
    case 'upload-file': {
      const parsed = uploadFilePayloadSchema.safeParse(payload)
      return parsed.success ? { type, payload: parsed.data } : null
    }
    case 'call-rule': {
      const parsed = callRulePayloadSchema.safeParse(payload)
      return parsed.success ? { type, payload: parsed.data } : null
    }
    case 'toast': {
      const parsed = toastPayloadSchema.safeParse(payload)
      return parsed.success ? { type, payload: parsed.data } : null
    }
    case 'open-url': {
      const parsed = openUrlPayloadSchema.safeParse(payload)
      return parsed.success ? { type, payload: parsed.data } : null
    }
    case 'get-viewer-config': {
      const parsed = getViewerConfigPayloadSchema.safeParse(payload)
      return parsed.success ? { type, payload: parsed.data } : null
    }
    case 'export-as-file': {
      const parsed = exportAsFilePayloadSchema.safeParse(payload)
      return parsed.success ? { type, payload: parsed.data } : null
    }
    default:
      return null
  }
}

// --- host -> page ----------------------------------------------------------

export type OutgoingMessage =
  | { __widget: true; type: 'data'; data: unknown }
  | { __widget: true; type: 'config'; config: unknown }
  | { __widget: true; type: 'config-fields'; configFields: unknown }
  | { __widget: true; type: 'result'; requestId: string; ok: true; data: unknown }
  | { __widget: true; type: 'result'; requestId: string; ok: false; error: string }

export function dataMessage(data: unknown): OutgoingMessage {
  return { __widget: true, type: 'data', data }
}

export function configMessage(config: unknown): OutgoingMessage {
  return { __widget: true, type: 'config', config }
}

export function configFieldsMessage(configFields: unknown): OutgoingMessage {
  return { __widget: true, type: 'config-fields', configFields }
}

export function resultMessage(
  requestId: string,
  result: { ok: true; data: unknown } | { ok: false; error: string }
): OutgoingMessage {
  return result.ok
    ? { __widget: true, type: 'result', requestId, ok: true, data: result.data }
    : { __widget: true, type: 'result', requestId, ok: false, error: result.error }
}

// --- pure transforms -------------------------------------------------------

/**
 * Join a page-supplied path onto the repo's download root (`appCtx.urlDownload`).
 *
 * The root comes from the host, never from the page, which is what keeps the
 * repository unforgeable (SECURITY-AUDIT F1/F12). Segments arrive as raw names
 * and are encoded here, so a caller passes "uuid/some.pdf" rather than
 * a pre-escaped URL. `.` and `..` segments are dropped — defence in depth, not
 * the boundary, since DownloadServlet builds a filesystem path from what it gets.
 */
export function repoFileUrl(urlDownload: string, path: string): string {
  const segments = path.split('/').filter((segment) => segment && segment !== '.' && segment !== '..')
  if (!segments.length) throw new Error('readFile: path is empty')
  return `${urlDownload.replace(/\/+$/, '')}/${segments.map(encodeURIComponent).join('/')}`
}

const EXPORT_MEDIA_TYPES: Record<string, string> = {
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  txt: 'text/plain',
  md: 'text/markdown',
  json: 'application/json',
  jsonld: 'application/ld+json',
  xml: 'application/xml',
  ttl: 'text/turtle',
  trig: 'application/trig',
  nt: 'application/n-triples',
  nq: 'application/n-quads',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  pdf: 'application/pdf',
  zip: 'application/zip',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
}

/**
 * The type a exportAsFile file is saved with: the page's own, else the one its extension names, else
 * application/octet-stream. A text type from the extension says UTF-8, which is what the bridge encodes a string as.
 */
export function exportMediaType(filename: string, declared?: string): string {
  if (declared && declared.trim()) return declared.trim()
  const extension = /\.([a-z0-9]+)$/i.exec(filename)?.[1].toLowerCase()
  const type = (extension && EXPORT_MEDIA_TYPES[extension]) || 'application/octet-stream'
  return type.startsWith('text/') ? `${type};charset=utf-8` : type
}

/**
 * The rule tags a page may start, from the widget's comma-separated option, and
 * the allow-list the `call-rule` message is matched against.
 *
 * Trimmed, because a builder writing "tagA, tagB" means two tags and the space
 * belongs to neither. Empty entries are dropped for the same reason: a trailing
 * comma is punctuation, not a tag.
 */
export function allowedRuleTagsFrom(configured: string | null | undefined): string[] {
  return (configured ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0)
}

/**
 * JSON that is safe to inline inside a `<script>` tag: escapes `<` so a value
 * containing `</script>` (or `-->`) can't break out of the element.
 */
export function serializeForScript(value: unknown): string {
  const json = (() => {
    try {
      return JSON.stringify(value === undefined ? null : value)
    } catch {
      return 'null'
    }
  })()
  return json.replace(/</g, '\\u003c').replace(/-->/g, '--\\u003e')
}

/**
 * Turn `wistor.updateQuads(args)` into the payload the data-update endpoint
 * takes, picked by `args.variant`.
 *
 * The page sends the endpoint's own triple shape, so there is no format to
 * convert: the only work here is filling in a triple's `graph` from
 * `fallbackGraph` (the per-request override or the widget's targetGraph) and
 * choosing which variant's field names to build. A triple that names no graph
 * with no fallback available fails loudly rather than being written to an
 * unknown graph.
 *
 * Deliberately does *not* run removeDuplicatePayloads: a page that asks to
 * delete and insert the same triple gets exactly that, in both variants. The
 * result is validated against the shared payload schemas, so a page can't push
 * a shape the endpoint would choke on.
 */
export function dataUpdatePayloadFromUpdateQuads(args: {
  payload: UpdateQuadsPayload
  fallbackGraph?: string
  logging?: Logging
}): DataUpdatePayload | StrictDataUpdateV1Payload {
  const { payload, fallbackGraph, logging } = args
  const variant = payload.variant ?? 'legacy'
  const deletes = withGraph(payload.deletes, fallbackGraph)

  if (variant === 'legacy') {
    /* `inserts` is accepted here as the pre-variant field name for `updates`. */
    const updates = withGraph(payload.updates ?? payload.inserts, fallbackGraph)
    requireTriples(updates, deletes)
    return dataUpdatePayloadSchema.parse({ updates, delete: deletes, logging })
  }

  if (variant === 'strict-v1') {
    if (payload.updates?.length && !payload.inserts?.length) {
      throw new Error(
        "updateQuads: strict-v1 takes the triples to add in `inserts`, and `updates` was filled instead. " +
        'Rename the field, or use the legacy variant if replacing every existing value of those properties is what you want.'
      )
    }
    const inserts = withGraph(payload.inserts, fallbackGraph)
    requireTriples(inserts, deletes)
    return strictDataUpdateV1PayloadSchema.parse({ variant, inserts, deletes, logging })
  }

  throw new Error(
    `updateQuads: unknown variant "${variant}". Expected 'legacy' (overwrites subject+predicate) ` +
    "or 'strict-v1' (removes only the exact triples in deletes)."
  )
}

/**
 * Keys held back from `wistor.getViewerConfig()`. `user` and `project` are the
 * account and project records the app was created under, which describe who owns
 * the app rather than how it is configured.
 */
const KEYS_WITHHELD_FROM_PAGE = ['user', 'project']

export type ViewerConfigForPage = Record<string, unknown> | null

/**
 * What `wistor.getViewerConfig()` hands the page: the app configuration as the
 * app stores it, so a coding agent writing the page works from the same picture
 * of the app a configurator has - the widget tree, the queries, the parameter
 * values, the layout.
 *
 * Everything is passed through except KEYS_WITHHELD_FROM_PAGE. Withholding more
 * would defeat the point: the config is what the page is being written against.
 *
 * The JSON round-trip is not cosmetic. postMessage throws on a value it cannot
 * structured-clone, and it throws in the host, where nothing answers the page,
 * so the page's promise would never settle. Round-tripping drops what cannot
 * survive the trip and turns the rest into an error the page actually receives.
 */
export function viewerConfigForPage(config: Record<string, unknown> | null | undefined): ViewerConfigForPage {
  if (config == null) return null

  const shared = Object.fromEntries(
    Object.entries(config).filter(([key]) => !KEYS_WITHHELD_FROM_PAGE.includes(key))
  )

  try {
    return JSON.parse(JSON.stringify(shared))
  } catch (error) {
    throw new Error(
      'getViewerConfig: the app config holds a value that cannot be sent to the page: ' + String(error)
    )
  }
}

/**
 * A write holding no triples is a valid request: the endpoint answers 200 with
 * an empty body, and the page's promise resolves exactly as a real write does.
 * A page therefore cannot tell a no-op from a write, so refuse it here and give
 * the page something it can act on.
 */
function requireTriples(inserts: TripleUpdatePayload[], deletes: TripleUpdatePayload[]): void {
  if (inserts.length > 0 || deletes.length > 0) return

  throw new Error(
    'updateQuads: nothing to write - the triples to add and the triples to delete are both empty.'
  )
}

function withGraph(triples: PageTriple[] | undefined, fallbackGraph: string | undefined): TripleUpdatePayload[] {
  if (!triples || triples.length === 0) return []

  return triples.map(function withResolvedGraph(triple) {
    const graph = triple.graph ?? fallbackGraph
    if (!graph) {
      throw new Error(
        'updateQuads: a triple names no graph and no target graph is configured. ' +
        "Set the widget's targetGraph, pass a graph in the update, or give the triple its own."
      )
    }
    return { ...triple, graph }
  })
}
